import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { ROLES, API_V1, authFetch } from '../constants';
import { realtimeService } from '../services/realtimeService';
import { Bell, Settings, ChevronDown } from 'lucide-react';
import KnowledgeSearch from './KnowledgeSearch';
import { NAV_SECTIONS } from './Sidebar';

// Map role id to display name
const ROLE_DISPLAY: Record<string, string> = {
  cfo: 'CFO Office',
  vp_finance: 'Finance Operations',
  controller: 'Accounting',
  treasury_mgr: 'Treasury',
  senior_accountant: 'Accounting',
  ap_supervisor: 'Finance Operations',
  analyst: 'Finance Operations',
};

function getInitials(name: string): string {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

function getFirstName(name: string): string {
  if (!name) return 'User';
  return name.trim().split(/\s+/)[0];
}

// Find current page label from nav sections
function getCurrentPageLabel(pathname: string): { section: string; page: string } | null {
  for (const section of NAV_SECTIONS) {
    for (const item of section.items) {
      if (item.path === '/' && pathname === '/') {
        return { section: section.header, page: item.label };
      }
      if (item.path !== '/' && pathname.startsWith(item.path)) {
        return { section: section.header, page: item.label };
      }
    }
  }
  return null;
}

interface TopBarProps {
  onHamburgerClick?: () => void;
}

export default function TopBar({ onHamburgerClick }: TopBarProps = {}) {
  const { role, setRole, entity, setEntity, entities, authUser, logout } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fetch unread notification count on mount + SSE push
  useEffect(() => {
    const fetchUnread = () => {
      authFetch(`${API_V1}/notifications/unread-count`)
        .then((r: Response) => r.ok ? r.json() : { unread_count: 0 })
        .then((d: any) => setUnreadCount(d.unread_count || 0))
        .catch(() => {});
    };
    fetchUnread();
    const unsub = realtimeService.on('notification', () => { fetchUnread(); });
    return () => { unsub(); };
  }, []);

  const userName = authUser ? getFirstName(authUser.full_name) : 'User';
  const userInitials = authUser ? getInitials(authUser.full_name) : '??';
  const currentPage = getCurrentPageLabel(location.pathname);

  const handleLogout = () => {
    setDropdownOpen(false);
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="topbar" style={{
      background: '#ffffff',
      borderBottom: '1px solid var(--border-subtle)',
      height: 48,
      padding: '0 24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}>
      {/* Left: Hamburger + Brand + Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        {onHamburgerClick && (
          <button className="hamburger-btn" onClick={onHamburgerClick} aria-label="Toggle menu">
            &#9776;
          </button>
        )}

        {/* AI active indicator */}
        <div className="hide-mobile" style={{ display: 'flex', alignItems: 'center', gap: 6, marginRight: 4 }}>
          <span className="ai-pulse" />
          <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--navy-600)', letterSpacing: '0.02em' }}>AI Active</span>
        </div>

        {/* Breadcrumb divider */}
        <div className="hide-mobile" style={{ width: 1, height: 20, background: 'var(--border-default)' }} />

        {/* Breadcrumb navigation */}
        {currentPage && (
          <div className="hide-mobile" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <span style={{ color: 'var(--text-muted)' }}>{currentPage.section}</span>
            <span style={{ color: 'var(--text-hint)' }}>/</span>
            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{currentPage.page}</span>
          </div>
        )}
      </div>

      {/* Center: Search (hidden on mobile) */}
      <div className="hide-mobile">
        <KnowledgeSearch />
      </div>

      {/* Right: Entity selector + Notifications + Avatar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {/* Entity selector — prominent, labeled, badge-style when scoped */}
        {entities && entities.length > 0 && (() => {
          const selected = entity ? entities.find((e: any) => e.entity_code === entity) : null;
          const isAll = !entity;
          return (
            <div
              className="hide-mobile"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 0,
                background: isAll ? 'var(--bg-surface)' : 'var(--accent-subtle)',
                border: `1px solid ${isAll ? 'var(--border-default)' : 'var(--navy-600)'}`,
                borderRadius: 8,
                overflow: 'hidden',
                boxShadow: isAll ? 'none' : '0 0 0 2px rgba(30, 58, 95, 0.08)',
                transition: 'all 0.15s',
              }}
              title={selected
                ? `Viewing data for ${selected.entity_code} — ${selected.entity_name} (${selected.functional_currency || ''})`
                : 'Viewing consolidated data across all entities'}
            >
              <span style={{
                padding: '6px 10px 6px 12px',
                fontSize: 10,
                fontWeight: 700,
                color: isAll ? 'var(--text-hint)' : 'var(--navy-800)',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                borderRight: '1px solid var(--border-subtle)',
                userSelect: 'none',
              }}>
                Entity
              </span>
              <select
                title="Legal entity scope"
                aria-label="Select legal entity to scope platform data"
                value={entity || ''}
                onChange={(e) => setEntity(e.target.value)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: '6px 10px',
                  fontSize: 13,
                  color: isAll ? 'var(--text-secondary)' : 'var(--navy-900)',
                  fontWeight: isAll ? 500 : 700,
                  cursor: 'pointer',
                  outline: 'none',
                  appearance: 'none',
                  paddingRight: 24,
                  minWidth: 140,
                }}
              >
                <option value="">All Entities</option>
                {entities.map((ent: any) => (
                  <option key={ent.entity_code} value={ent.entity_code}>
                    {ent.entity_code} — {ent.entity_name}{ent.functional_currency ? ` (${ent.functional_currency})` : ''}
                  </option>
                ))}
              </select>
              <ChevronDown style={{ width: 14, height: 14, color: isAll ? 'var(--text-hint)' : 'var(--navy-800)', marginRight: 8, marginLeft: -20, pointerEvents: 'none' }} />
            </div>
          );
        })()}

        {/* Notification bell */}
        <button
          type="button"
          aria-label="Notifications"
          onClick={() => navigate('/operations?tab=Notifications')}
          style={{
            cursor: 'pointer', position: 'relative',
            width: 34, height: 34, borderRadius: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.15s',
            background: 'transparent', border: 'none', padding: 0,
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          title="Notifications"
        >
          <Bell style={{ width: 16, height: 16, color: 'var(--text-muted)' }} />
          {unreadCount > 0 && (
            <span style={{
              position: 'absolute', top: 2, right: 2,
              background: 'var(--negative)', color: 'var(--text-primary)',
              fontSize: 9, fontWeight: 700,
              minWidth: 16, height: 16, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '0 4px', border: '2px solid var(--bg-base)',
            }}>
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        {/* User avatar + dropdown */}
        <div
          ref={dropdownRef}
          onClick={() => setDropdownOpen(!dropdownOpen)}
          role="button"
          tabIndex={0}
          aria-label="User menu"
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDropdownOpen(!dropdownOpen); } }}
          style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
        >
          <span className="hide-mobile" style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>
            {userName}
          </span>
          <div style={{
            width: 34, height: 34, borderRadius: 10,
            background: 'linear-gradient(135deg, var(--navy-900), var(--navy-600))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 600, color: 'var(--text-primary)',
          }}>
            {userInitials}
          </div>
          <ChevronDown style={{ width: 14, height: 14, color: 'var(--text-hint)' }} className="hide-mobile" />

          {dropdownOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: 8,
              background: 'var(--bg-base)',
              border: '1px solid var(--border-default)',
              borderRadius: 12,
              minWidth: 240,
              zIndex: 1000,
              overflow: 'hidden',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            }}>
              {/* User info */}
              {authUser && (
                <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>
                    {authUser.full_name}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {authUser.email}
                  </div>
                  <div style={{
                    fontSize: 11, color: 'var(--navy-800)', marginTop: 6,
                    textTransform: 'uppercase' as const, letterSpacing: '0.05em', fontWeight: 600,
                    background: 'var(--accent-subtle)', display: 'inline-block',
                    padding: '2px 8px', borderRadius: 6,
                  }}>
                    {authUser.role}
                  </div>
                </div>
              )}

              {/* Role switcher */}
              <div style={{
                padding: '10px 16px 4px', fontSize: 10, fontWeight: 600,
                color: 'var(--text-hint)', textTransform: 'uppercase' as const, letterSpacing: '1px',
              }}>
                Switch Role
              </div>
              {ROLES.map((r: any) => (
                <div
                  key={r.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    setRole(r);
                    setDropdownOpen(false);
                  }}
                  style={{
                    padding: '8px 16px',
                    fontSize: 13,
                    fontWeight: 500,
                    color: role.id === r.id ? 'var(--navy-800)' : 'var(--text-secondary)',
                    cursor: 'pointer',
                    background: role.id === r.id ? 'var(--accent-subtle)' : 'transparent',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                  onMouseLeave={e => e.currentTarget.style.background = role.id === r.id ? 'var(--accent-subtle)' : 'transparent'}
                >
                  {r.label}
                  <span style={{ fontSize: 11, color: 'var(--text-hint)', marginLeft: 6 }}>{r.level}</span>
                </div>
              ))}

              {/* Logout */}
              <div style={{ borderTop: '1px solid var(--border-subtle)', marginTop: 4 }}>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLogout();
                  }}
                  style={{
                    padding: '10px 16px',
                    fontSize: 13,
                    fontWeight: 500,
                    color: 'var(--negative)',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  Sign Out
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
